package com.arena_web.analytics

import com.arena_web.core.config.AppConfig
import com.arena_web.data.api.AnalyticsApi
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.delay
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag
import kotlin.js.Date
import kotlin.random.Random

/**
 * Client-side analytics tracker.
 *
 * Non-blocking and never throws — every public entry point is wrapped in
 * [runCatching] so a broken analytics pipeline can never take the app down.
 *
 * - Events accumulate in an in-memory queue (cap 500, oldest dropped) that is
 *   mirrored to localStorage after every mutation so page reloads lose nothing.
 * - The queue is flushed every 15s, or as soon as 20 events are pending, in
 *   batches of at most 50. Failed batches simply stay queued for the next tick.
 * - Sessions: a UUID that renews after 30 minutes of inactivity (tracked via a
 *   persisted last-event timestamp, so reopening a tab within 30 min continues
 *   the same session). `session_start` is emitted automatically on renewal.
 * - On `beforeunload` / `visibilitychange(hidden)` the remaining queue is
 *   persisted and handed to `navigator.sendBeacon` (which can't carry an
 *   Authorization header — the endpoint accepts anonymous batches).
 *
 * Wired as a singleton `object` — same pattern as [Storage] / [AppConfig] —
 * because it must be reachable from the route interceptor in `initKobweb`
 * (which runs before any composition) and from deep UI click handlers.
 * [init] is called once at app startup from `AppEntry`.
 */
object AnalyticsTracker {

    private const val QUEUE_CAP = 500
    private const val FLUSH_THRESHOLD = 20
    private const val MAX_BATCH_SIZE = 50
    private const val FLUSH_INTERVAL_MS = 15_000L
    private const val SESSION_TIMEOUT_MS = 30L * 60L * 1000L
    private const val MAX_PARAMS = 20
    private const val MAX_PARAM_KEY_LENGTH = 40
    private const val MAX_PARAM_VALUE_LENGTH = 200

    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = true
        explicitNulls = false
    }

    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Main)

    private var api: AnalyticsApi? = null
    private var flushJob: Job? = null
    private var isFlushing = false
    private var queueRestored = false
    private var sessionId: String? = null
    private var lastTrackedScreen: String? = null

    private val queue = ArrayDeque<AnalyticsEventDto>()

    private val deviceId: String by lazy {
        Storage.get(StorageKeys.ANALYTICS_DEVICE_ID) ?: randomUuid().also {
            Storage.save(StorageKeys.ANALYTICS_DEVICE_ID, it)
        }
    }

    // Derived once per page load — viewport + user agent heuristic.
    private val deviceType: String by lazy { detectDeviceType() }

    private val locale: String by lazy {
        runCatching { window.navigator.language }.getOrNull()?.takeIf { it.isNotBlank() } ?: "en"
    }

    // ── Public API ──

    /**
     * Idempotent one-time wiring: restores the persisted queue, registers the
     * page-close/visibility/popstate handlers, emits `app_open` and starts the
     * periodic flush loop. Events tracked before init (e.g. the initial
     * `screen_view` from the route interceptor) are queued and flushed after.
     */
    fun init(api: AnalyticsApi) {
        if (this.api != null) return
        this.api = api
        runCatching {
            ensureQueueRestored()
            registerLifecycleHandlers()
            enqueue("app_open", screen = lastTrackedScreen)
            startFlushLoop()
        }
    }

    fun trackScreen(screen: String, params: Map<String, String> = emptyMap()) {
        runCatching {
            enqueue("screen_view", screen = sanitizeName(screen), params = params)
        }
    }

    fun trackClick(element: String, screen: String? = null, params: Map<String, String> = emptyMap()) {
        runCatching {
            val sanitized = sanitizeName(element)
            enqueue(
                name = "click.$sanitized",
                screen = screen?.let(::sanitizeName) ?: lastTrackedScreen,
                params = params,
            )
        }
    }

    fun flush() {
        scope.launch { runCatching { flushQueue() } }
    }

    /**
     * Central route hook — called by the Kobweb route interceptor (initial
     * load + every `navigateTo`) and the `popstate` listener (browser
     * back/forward). Deduplicates consecutive repeats of the same screen so
     * query-param-only navigations don't spam `screen_view`s.
     */
    fun onRouteChanged(path: String) {
        runCatching {
            val screen = screenNameForRoute(path)
            if (screen == lastTrackedScreen) return
            lastTrackedScreen = screen
            trackScreen(screen)
        }
    }

    // ── Route → screen name ──

    // uuid-ish / numeric path segments are ids, not screen name material.
    private val idSegmentRegex = Regex("^(\\d+|[0-9a-fA-F-]{8,})$")

    private fun screenNameForRoute(path: String): String {
        val cleanPath = path.substringBefore('?').substringBefore('#')
        val segments = cleanPath.split('/').filter { it.isNotEmpty() }
        if (segments.isEmpty()) return "home"

        val static = segments.filterNot { idSegmentRegex.matches(it) }
        val hadId = static.size < segments.size
        val base = (if (static.isEmpty()) segments else static).joinToString("_")

        val name = if (hadId) {
            when (base) {
                "events" -> "event_details"
                "clubs" -> "club_details"
                "chat" -> "chat_room"
                "profile" -> "user_profile"
                "groups" -> "group_details"
                "news" -> "news_details"
                else -> "${base}_details"
            }
        } else base

        return sanitizeName(name)
    }

    // ── Queueing / sessions ──

    private fun enqueue(name: String, screen: String?, params: Map<String, String> = emptyMap()) {
        ensureQueueRestored()
        val now = Date.now().toLong()
        val session = ensureSession(now, screen)
        queue.addLast(
            AnalyticsEventDto(
                name = name,
                clientTs = now,
                sessionId = session,
                screen = screen,
                params = sanitizeParams(params),
            )
        )
        while (queue.size > QUEUE_CAP) queue.removeFirst()
        Storage.save(StorageKeys.ANALYTICS_LAST_EVENT_AT, now.toString())
        persistQueue()
        if (queue.size >= FLUSH_THRESHOLD) flush()
    }

    /**
     * Returns the current session id, starting a fresh session (and queueing
     * `session_start`) on the first event or after >= 30 min of inactivity.
     */
    private fun ensureSession(now: Long, screen: String?): String {
        val stored = sessionId ?: Storage.get(StorageKeys.ANALYTICS_SESSION_ID)
        val lastEventAt = Storage.get(StorageKeys.ANALYTICS_LAST_EVENT_AT)?.toLongOrNull()
        val expired = lastEventAt == null || now - lastEventAt >= SESSION_TIMEOUT_MS
        if (stored != null && !expired) {
            sessionId = stored
            return stored
        }
        val fresh = randomUuid()
        sessionId = fresh
        Storage.save(StorageKeys.ANALYTICS_SESSION_ID, fresh)
        queue.addLast(
            AnalyticsEventDto(
                name = "session_start",
                clientTs = now,
                sessionId = fresh,
                screen = screen,
            )
        )
        return fresh
    }

    // ── Flushing ──

    private fun startFlushLoop() {
        flushJob?.cancel()
        flushJob = scope.launch {
            while (isActive) {
                delay(FLUSH_INTERVAL_MS)
                runCatching { flushQueue() }
            }
        }
    }

    private suspend fun flushQueue() {
        val api = api ?: return
        if (isFlushing) return
        isFlushing = true
        try {
            while (queue.isNotEmpty()) {
                val batch = queue.take(MAX_BATCH_SIZE)
                val accepted = runCatching { api.sendBatch(buildRequest(batch)) }.getOrDefault(false)
                if (!accepted) break // keep failed batch queued for the next tick
                repeat(batch.size) { queue.removeFirst() }
                persistQueue()
            }
        } finally {
            isFlushing = false
        }
    }

    private fun buildRequest(events: List<AnalyticsEventDto>) = AnalyticsBatchRequest(
        deviceId = deviceId,
        appVersion = AppConfig.appVersion.take(32),
        deviceType = deviceType,
        locale = locale,
        events = events,
    )

    // ── Page close / visibility ──

    private fun registerLifecycleHandlers() {
        window.addEventListener("beforeunload", {
            runCatching { drainOnPageHide(emitBackground = false) }
        })
        document.addEventListener("visibilitychange", {
            if (document.asDynamic().visibilityState == "hidden") {
                runCatching { drainOnPageHide(emitBackground = true) }
            }
        })
        // Kobweb handles browser back/forward via window.onpopstate directly
        // (bypassing route interceptors), so mirror it here.
        window.addEventListener("popstate", {
            runCatching { onRouteChanged(window.location.pathname) }
        })
    }

    private fun drainOnPageHide(emitBackground: Boolean) {
        if (emitBackground) enqueue("app_background", screen = lastTrackedScreen)
        // localStorage backup first — if the beacon is dropped, the next page
        // load restores and re-sends the queue.
        persistQueue()
        sendPendingViaBeacon()
    }

    private fun sendPendingViaBeacon() {
        if (queue.isEmpty()) return
        val batch = queue.take(MAX_BATCH_SIZE)
        val body = json.encodeToString(buildRequest(batch))
        // sendBeacon can't set an Authorization header — the endpoint accepts
        // anonymous batches, so the payload goes out exactly as usual.
        val blob = Blob(arrayOf(body), BlobPropertyBag(type = "application/json"))
        val sent = window.navigator.asDynamic()
            .sendBeacon(AnalyticsApi.batchUrl(), blob) as? Boolean ?: false
        if (sent) {
            repeat(batch.size) { queue.removeFirst() }
            persistQueue()
        }
    }

    // ── Persistence ──

    private fun ensureQueueRestored() {
        if (queueRestored) return
        queueRestored = true
        val raw = Storage.get(StorageKeys.ANALYTICS_QUEUE) ?: return
        runCatching { json.decodeFromString<List<AnalyticsEventDto>>(raw) }
            .onSuccess { restored ->
                // Restored events predate anything queued this page load.
                restored.asReversed().forEach { queue.addFirst(it) }
                while (queue.size > QUEUE_CAP) queue.removeFirst()
            }
            .onFailure { Storage.remove(StorageKeys.ANALYTICS_QUEUE) }
    }

    private fun persistQueue() {
        runCatching {
            if (queue.isEmpty()) {
                Storage.remove(StorageKeys.ANALYTICS_QUEUE)
            } else {
                Storage.save(StorageKeys.ANALYTICS_QUEUE, json.encodeToString(queue.toList()))
            }
        }
    }

    // ── Sanitization / helpers ──

    /** Lowercase `[a-z0-9_-]+` — anything else collapses to `_`. */
    private fun sanitizeName(value: String): String =
        value.lowercase()
            .replace(Regex("[^a-z0-9_-]+"), "_")
            .trim('_')
            .take(64)
            .ifEmpty { "unknown" }

    private fun sanitizeParams(params: Map<String, String>): Map<String, String> {
        if (params.isEmpty()) return params
        return params.entries
            .take(MAX_PARAMS)
            .associate { (key, value) -> key.take(MAX_PARAM_KEY_LENGTH) to value.take(MAX_PARAM_VALUE_LENGTH) }
    }

    private fun detectDeviceType(): String = runCatching {
        val ua = window.navigator.userAgent.lowercase()
        val width = window.innerWidth
        val hasTouch = (window.navigator.asDynamic().maxTouchPoints as? Int ?: 0) > 0
        when {
            ua.contains("ipad") || ua.contains("tablet") -> "tablet"
            hasTouch && width < 768 -> "mobile"
            ua.contains("mobi") && hasTouch -> "mobile"
            hasTouch && width in 768..1024 -> "tablet"
            else -> "desktop"
        }
    }.getOrDefault("desktop")

    private fun randomUuid(): String {
        val crypto = window.asDynamic().crypto
        if (crypto != null && crypto.randomUUID != undefined) {
            runCatching { return crypto.randomUUID() as String }
        }
        // RFC-4122 v4 fallback for older browsers / non-secure contexts.
        val hex = "0123456789abcdef"
        return buildString {
            repeat(36) { i ->
                when (i) {
                    8, 13, 18, 23 -> append('-')
                    14 -> append('4')
                    19 -> append(hex[Random.nextInt(4) or 8])
                    else -> append(hex[Random.nextInt(16)])
                }
            }
        }
    }
}
